/**
 * Shared primitives for every Story Studio contract (ARCHITECTURE.md §4.0, §4.2).
 *
 * This file is the single source of truth for the coordinate convention:
 * every stored or transmitted coordinate is a percent of its containing box,
 * `0.00 … 100.00`, origin top-left, x to the right, y down. Real-world sizes
 * are centimetres, confidence/scale factors are unitless `0..1`, time is
 * seconds. Pixels never appear in a contract.
 *
 * Schemas that carry `.meta({ id })` are emitted as `$defs` in the generated
 * JSON Schema files, so the same primitive is referenced (not copied) wherever
 * it appears.
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Percent coordinates
// ---------------------------------------------------------------------------

/** Number of decimals a stored percent coordinate carries (§4.0: "2 decimals"). */
export const PCT_DECIMALS = 2;

const PCT_SCALE = 10 ** PCT_DECIMALS;

/**
 * Round a percent value to the contract's precision (2 decimals) and clip it
 * to `[0, 100]`. Writers (the editor's save path, the proposal merge, any
 * heuristic) call this before a value enters a document; `Pct` itself only
 * checks the range so that a document written by a slightly sloppy producer
 * still *reads* (see `PctQuantised` for the strict form).
 */
export function roundPct(value: number): number {
  const clipped = Math.min(100, Math.max(0, value));
  // `+ EPSILON` nudges exact .xx5 boundaries (which binary floats store just
  // below the half) so they round half-up: 1.005 → 1.01, not 1.00.
  return Math.round((clipped + Number.EPSILON) * PCT_SCALE) / PCT_SCALE;
}

/** True when `value` already sits on the 2-decimal grid (tolerates float noise). */
export function isQuantisedPct(value: number): boolean {
  if (!Number.isFinite(value)) return false;
  const scaled = value * PCT_SCALE;
  return Math.abs(scaled - Math.round(scaled)) < 1e-6;
}

/** Percent of the containing box, `0..100`. Range-checked; precision is the writer's job (`roundPct`). */
export const Pct = z
  .number()
  .min(0)
  .max(100)
  .meta({ id: 'Pct', description: 'Percent of the containing box, 0..100, 2 decimals.' });
export type Pct = z.infer<typeof Pct>;

/** Strict variant of `Pct` used on write paths: rejects more than 2 decimals. */
export const PctQuantised = Pct.refine(isQuantisedPct, {
  message: `Percent coordinates carry at most ${PCT_DECIMALS} decimals; call roundPct() first.`,
});

/** `[x, y]` in percent of the containing box. */
export const Pt = z.tuple([Pct, Pct]).meta({ id: 'Pt', description: '[x, y] percent point.' });
export type Pt = z.infer<typeof Pt>;

/** Open ring (no repeated closing vertex), at least 3 vertices. Winding and self-intersection are lints, not schema rules. */
export const Ring = z.array(Pt).min(3).meta({ id: 'Ring', description: 'Open polygon ring, >= 3 vertices.' });
export type Ring = z.infer<typeof Ring>;

/** Polygon with an outer ring (clockwise in screen space) and optional holes (counter-clockwise). */
export const Poly = z
  .strictObject({
    outer: Ring,
    holes: z.array(Ring).optional(),
  })
  .meta({ id: 'Poly' });
export type Poly = z.infer<typeof Poly>;

export const roundPt = (p: Pt): Pt => [roundPct(p[0]), roundPct(p[1])];
export const roundRing = (r: Ring): Ring => r.map(roundPt);
export const roundPoly = (p: Poly): Poly =>
  p.holes === undefined
    ? { outer: roundRing(p.outer) }
    : { outer: roundRing(p.outer), holes: p.holes.map(roundRing) };

// ---------------------------------------------------------------------------
// Scalars
// ---------------------------------------------------------------------------

/** Unitless `0..1` (confidence, scale factors, parallax caps live elsewhere). */
export const Unit = z.number().min(0).max(1);

/** Seconds, never negative (durations, holds, offsets). */
export const Seconds = z.number().min(0);

/** Non-negative integer (revisions, ids, counts). */
export const NonNegInt = z.int().min(0);

/** Positive integer (row ids, frame counts). */
export const PosInt = z.int().min(1);

/** Non-empty identifier string (feature ids, uids, handles). */
export const Id = z.string().min(1);

/**
 * ISO-8601 timestamp. The backend writes UTC with a `Z` suffix; the parser
 * also tolerates an explicit offset or a naive local time so a document is
 * never unreadable because of a formatting slip. The emitted JSON Schema uses
 * `format: date-time` (see `scripts/emit-schemas.ts`).
 */
export const IsoDateTime = z.iso.datetime({ offset: true, local: true }).meta({ id: 'IsoDateTime' });

// ---------------------------------------------------------------------------
// Closed enums (§4.2, §4.4.5)
// ---------------------------------------------------------------------------

export const Facing = z.enum(['left', 'right', 'camera', 'away', '3q_left', '3q_right']).meta({ id: 'Facing' });
export type Facing = z.infer<typeof Facing>;

export const Source = z.enum(['seed', 'heuristic', 'ai', 'human']);
export type Source = z.infer<typeof Source>;

export const ReviewStatus = z.enum(['seed', 'proposed', 'draft', 'reviewed', 'published']);
export type ReviewStatus = z.infer<typeof ReviewStatus>;

export const PublishedTier = z.enum(['auto', 'reviewed']);
export type PublishedTier = z.infer<typeof PublishedTier>;

export const AssetKind = z.enum(['background', 'live_background', 'character', 'object', 'audio']);
export type AssetKind = z.infer<typeof AssetKind>;

/** The three kinds the `/annotate/$kind/$uid` editor dispatches on. A rig has its own editor. */
export const AnnotationKind = z.enum(['stage', 'character', 'object']);
export type AnnotationKind = z.infer<typeof AnnotationKind>;

/**
 * Every document kind the `annotations` table accepts — the three above plus `rig`.
 *
 * `rig` is kept only because migration 0006 (forward-only) widened the `annotations.kind` /
 * `proposals.kind` CHECKs to it. The addendum rig track that wrote those rows was removed
 * (docs/SKEL-STORY.md P7): puppets are skel projects, not annotations, and no code writes or
 * reads `kind='rig'` any more. `AnnotationKind` stays at three.
 */
export const DocumentKind = z.enum(['stage', 'character', 'object', 'rig']);
export type DocumentKind = z.infer<typeof DocumentKind>;

export const LintSeverity = z.enum(['error', 'warn', 'info']);
export type LintSeverity = z.infer<typeof LintSeverity>;

// ---------------------------------------------------------------------------
// Provenance, asset reference, lint
// ---------------------------------------------------------------------------

/** Who/what wrote a feature (or a whole document) and how sure it was. Human = confidence 1. */
export const Provenance = z
  .strictObject({
    source: Source,
    confidence: Unit,
    at: IsoDateTime,
    /** 'user:operator' | 'claude:<model>' | 'heuristic:<name>@<ver>' | 'seed:sidecar@1' */
    by: Id,
    job_id: PosInt.optional(),
    /** True once a human touched the feature; re-runs never replace it. */
    locked: z.boolean().optional(),
  })
  .meta({ id: 'Provenance' });
export type Provenance = z.infer<typeof Provenance>;

export const AssetRef = z
  .strictObject({
    uid: Id,
    key: Id,
    etag: z.string().optional(),
    kind: AssetKind,
  })
  .meta({ id: 'AssetRef' });
export type AssetRef = z.infer<typeof AssetRef>;

/**
 * A lint pill. `message` is REQUIRED plain English (§4.3.7 `LINT_TEXT`);
 * `fix_hint` is the plain-English action. `ref` points at the feature id.
 */
export const Lint = z
  .strictObject({
    code: Id,
    severity: LintSeverity,
    ref: z.string().nullable().optional(),
    message: z.string().min(1),
    fix_hint: z.string().optional(),
  })
  .meta({ id: 'Lint' });
export type Lint = z.infer<typeof Lint>;

// ---------------------------------------------------------------------------
// Envelope
// ---------------------------------------------------------------------------

/** Contract major version baked into `schema: "story-studio.<kind>/1"`. Bump on a breaking change. */
export const CONTRACT_VERSION = 1;

/**
 * Build the annotation envelope for one document kind (§4.2 `Envelope<K, B>`).
 *
 * `published_tier` / `published_rev` are server-assigned and required on the
 * wire (the emitted JSON Schema lists them in `required`); the client parser
 * defaults them to `null` so an abridged or hand-written document still parses.
 */
export function envelope<K extends DocumentKind, B extends z.ZodType>(kind: K, body: B) {
  return z.strictObject({
    schema: z.literal(`story-studio.${kind}/${CONTRACT_VERSION}` as `story-studio.${K}/1`),
    asset: AssetRef,
    /** draft_rev of the head this body was loaded from; PUT sends it back as base_rev. */
    rev: NonNegInt,
    status: ReviewStatus,
    published_tier: PublishedTier.nullable().default(null),
    published_rev: NonNegInt.nullable().default(null),
    /** Vocabulary revision the body was authored against. */
    vocab_rev: NonNegInt,
    /** Last writer of the document as a whole. */
    provenance: Provenance,
    body,
  });
}
